<template>
  <v-dialog :model-value="open" max-width="760" scrollable aria-labelledby="shipping-details-title" @update:model-value="(value: boolean) => !value && emit('close')">
    <div ref="root" class="flex max-h-[90vh] flex-col rounded-xl bg-white shadow-xl">
      <header class="border-b border-gray-200 px-5 py-4">
        <h2 id="shipping-details-title" class="m-0 text-lg font-semibold text-gray-900">Shipping details</h2>
        <p class="m-0 mt-1 text-sm text-gray-500">
          Shipped {{ fmtDay(ev.shippedOn) }} · {{ ev.forwarder }}
          <template v-if="poNumbers.length">· {{ poNumbers.join(', ') }}</template>
        </p>
      </header>

      <div class="flex flex-col gap-5 overflow-y-auto px-5 py-4">
        <section class="flex flex-col gap-1">
          <template v-if="partner">
            <label class="label" for="shipping-details-hawb">HAWB/BOL</label>
            <div class="flex flex-wrap gap-2">
              <input
                id="shipping-details-hawb"
                v-model="hawb"
                class="input h-9 min-w-0 flex-1 basis-64 font-mono"
                :class="bad('hawb')"
                :maxlength="HAWB_MAX"
                placeholder="e.g. LKY26101701"
                data-field="hawb"
                :aria-invalid="error?.field === 'hawb'"
                :aria-describedby="describedBy('hawb')"
                @keydown.enter.prevent="saveHawb"
              />
              <button type="button" class="btn-secondary !py-1.5" :disabled="savingHawb" @click="saveHawb">{{ hawbLabel }}</button>
            </div>
            <span v-if="error?.field === 'hawb'" :id="errorId('hawb')" class="text-xs text-red-700">{{ error.message }}</span>
          </template>
          <p v-else class="m-0 text-sm text-gray-700">
            HAWB/BOL:
            <span v-if="ev.hawb" class="font-mono text-gray-900">{{ ev.hawb }}</span>
            <span v-else class="text-gray-500">No HAWB/BOL yet</span>
          </p>
        </section>

        <section class="flex flex-col gap-2">
          <div class="flex flex-wrap items-baseline gap-x-3">
            <h3 class="m-0 text-sm font-semibold text-gray-900">Packing lists</h3>
            <span v-if="versions.length === 0" class="text-sm font-semibold text-amber-800">Packing list missing</span>
          </div>
          <ul v-if="versions.length" class="m-0 flex list-none flex-col gap-1.5 p-0">
            <li
              v-for="v in versions"
              :key="v.id"
              class="flex items-start gap-3 rounded-lg border px-3 py-2 text-sm"
              :class="v.id === selectedId ? 'border-blue-200 bg-blue-50' : 'border-gray-200'"
            >
              <span
                class="mt-px inline-flex h-5 flex-none items-center rounded px-1.5 text-xs font-semibold tabular-nums"
                :class="v.id === selectedId ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'"
              >
                v{{ v.version }}
              </span>
              <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="break-all font-medium text-gray-900">{{ v.fileName }}</span>
                <span class="text-xs text-gray-500">Uploaded by {{ v.uploadedBy }} · {{ fmtDate(v.uploadedAt.slice(0, 10)) }}</span>
                <span v-if="v.note" class="break-words text-xs text-gray-700">{{ v.note }}</span>
              </div>
              <span class="flex flex-none flex-col items-end gap-1 text-xs sm:flex-row sm:items-center sm:gap-3">
                <button v-if="v.id !== selectedId" type="button" class="font-semibold text-blue-700 hover:underline" @click="select(v.id)">Compare</button>
                <span v-else class="text-gray-600">Compared below</span>
                <button
                  type="button"
                  class="font-semibold text-blue-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="downloading === v.id"
                  @click="download(v.id)"
                >
                  {{ downloading === v.id ? 'Downloading…' : 'Download' }}
                </button>
              </span>
            </li>
          </ul>
          <!-- The upload sits under the list it adds to: one place, no second heading. -->
          <template v-if="partner">
            <div class="flex flex-wrap items-start gap-2">
              <PackingListInput
                id="shipping-details-packing-list"
                v-model="file"
                class="min-w-0 flex-1 basis-64"
                :hint="versions.length ? 'Newer version · .xlsx or .csv, up to 10 MB' : '.xlsx or .csv, up to 10 MB'"
                :error="error?.field === 'file' ? error.message : null"
              />
              <button type="button" class="btn-primary !py-1.5" :disabled="uploading" @click="upload">{{ uploadLabel }}</button>
            </div>
            <div v-if="versions.length" class="flex flex-col gap-1">
              <label class="label flex flex-col gap-1">
                What changed
                <input
                  v-model="note"
                  class="input h-9"
                  :class="bad('note')"
                  :maxlength="NOTE_MAX"
                  placeholder="e.g. pallet 3 relabelled, PO numbers added"
                  data-field="note"
                  :aria-invalid="error?.field === 'note'"
                  :aria-describedby="describedBy('note')"
                />
              </label>
              <span v-if="error?.field === 'note'" :id="errorId('note')" class="text-xs text-red-700">{{ error.message }}</span>
            </div>
            <p v-if="uploadError" class="m-0 text-sm text-red-700" role="alert">{{ uploadError }}</p>
          </template>
        </section>

        <section v-if="selectedId" class="flex flex-col gap-2">
          <h3 class="m-0 text-sm font-semibold text-gray-900">
            Planned vs packing list
            <template v-if="selected">(v{{ selected.version }})</template>
          </h3>
          <p class="sr-only" role="status">{{ selected || selectedError ? '' : 'Loading…' }}</p>
          <PackingListComparison v-if="selected" :packing-list="selected" :shipments="eventShipments" :po-details="eventDetails" />
          <p v-else-if="selectedError" class="m-0 text-sm text-red-700" role="alert">
            {{ selectedError }}
            <button type="button" class="ml-2 font-medium underline" @click="retrySelected">Retry</button>
          </p>
          <v-progress-linear v-else indeterminate color="primary" height="2" aria-label="Loading packing list" />
        </section>
      </div>

      <footer class="flex justify-end border-t border-gray-200 px-5 py-3">
        <button type="button" class="btn-secondary" @click="emit('close')">Close</button>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';

  import PackingListComparison from '@/components/poTracker/PackingListComparison.vue';
  import PackingListInput from '@/components/poTracker/PackingListInput.vue';
  import { useAccess } from '@/composables/useAccess';
  import { useFieldError } from '@/composables/useFieldError';
  import logger from '@/services/logger';
  import { fetchPackingList, fetchPoDetail, packingListDownloadUrl, setHawb, uploadPackingList } from '@/services/poTrackerService';
  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import { PERMISSIONS } from '@/types/access';
  import type { PackingList, PoDetail, ShippingEvent } from '@/types/poTracker';
  import { extractErrorMessage } from '@/utils/apiError';
  import { fmtDate, fmtDay, packingListErrorMessage, packingListFileError, shipmentsOnEvent, type FieldError } from '@/utils/poTracker';

  const HAWB_MAX = 60;
  const NOTE_MAX = 500;

  const props = defineProps<{ open: boolean; event: ShippingEvent; vendorId: string }>();
  const emit = defineEmits<{ close: [] }>();

  const store = usePoTrackerStore();
  const { hasPermission } = useAccess();
  const partner = computed(() => hasPermission(PERMISSIONS.WRITE_PO_SHIPMENTS));

  const root = ref<HTMLElement | null>(null);
  const ev = ref<ShippingEvent>(props.event);
  const hawb = ref(props.event.hawb ?? '');
  const file = ref<File | null>(null);
  const note = ref('');
  const error = ref<FieldError | null>(null);
  const uploadError = ref<string | null>(null);
  const savingHawb = ref(false);
  const uploading = ref(false);
  const downloading = ref<string | null>(null);
  // Packing lists by id, fetched once each; an upload returns its rows directly.
  const lists = ref<Map<string, PackingList>>(new Map());
  // Why a version's rows failed to load, by id; cleared when it is retried.
  const loadErrors = ref<Map<string, string>>(new Map());
  // Details of the event's POs that the page hasn't loaded (their panels are closed).
  const fetchedDetails = ref<Map<string, PoDetail>>(new Map());

  const versions = computed(() => [...ev.value.packingLists].sort((a, b) => b.version - a.version));
  const selectedId = ref<string | null>(versions.value[0]?.id ?? null);
  const selected = computed(() => (selectedId.value ? (lists.value.get(selectedId.value) ?? null) : null));
  const selectedError = computed(() => (selectedId.value ? (loadErrors.value.get(selectedId.value) ?? null) : null));

  const poNumbers = computed(() => ev.value.poNumbers);
  // The store's cache wins: it is refreshed after every write on an open panel.
  const eventDetails = computed(() => poNumbers.value.flatMap((po) => store.cachedDetail(po) ?? fetchedDetails.value.get(po) ?? []));
  const eventShipments = computed(() => shipmentsOnEvent(ev.value.id, eventDetails.value));

  const hawbLabel = computed(() => {
    if (savingHawb.value) return 'Saving…';
    return ev.value.hawb ? 'Save' : 'Add HAWB/BOL';
  });
  const uploadLabel = computed(() => {
    if (uploading.value) return 'Uploading…';
    return versions.value.length ? 'Upload version' : 'Upload';
  });

  const { errorId, describedBy, bad } = useFieldError(error);

  watch(selectedId, (id) => void (id && load(id)), { immediate: true });
  // Picking a file answers "choose a file"; the error would otherwise sit under the staged file until the next Upload.
  watch(file, (picked) => {
    if (picked && error.value?.field === 'file') error.value = null;
  });
  void loadEventPos();

  async function loadEventPos(): Promise<void> {
    const missing = poNumbers.value.filter((po) => store.cachedDetail(po) === undefined);
    // A failed fetch is toasted globally; the comparison then lacks that PO's planned pieces.
    const loaded = await Promise.all(missing.map((po) => fetchPoDetail(props.vendorId, po).catch(() => null)));
    const next = new Map(fetchedDetails.value);
    loaded.forEach((d) => d && next.set(d.poNumber, d));
    fetchedDetails.value = next;
  }

  async function load(id: string): Promise<void> {
    if (lists.value.has(id)) return;
    setLoadError(id, null);
    try {
      remember(await fetchPackingList(id));
    } catch (loadError) {
      setLoadError(id, packingListErrorMessage(loadError));
    }
  }

  function retrySelected(): void {
    if (selectedId.value) void load(selectedId.value);
  }

  function setLoadError(id: string, message: string | null): void {
    const next = new Map(loadErrors.value);
    if (message) next.set(id, message);
    else next.delete(id);
    loadErrors.value = next;
  }

  function remember(list: PackingList): void {
    lists.value = new Map(lists.value).set(list.id, list);
  }

  function select(id: string): void {
    selectedId.value = id;
  }

  // The signed URL is served as an attachment, so navigating to it downloads the file and keeps
  // the page; no new tab means no popup blocker.
  async function download(id: string): Promise<void> {
    if (downloading.value) return;
    downloading.value = id;
    try {
      window.location.assign(await packingListDownloadUrl(id));
    } catch (downloadError) {
      logger.showError(packingListErrorMessage(downloadError));
    } finally {
      downloading.value = null;
    }
  }

  function focusField(field: string): void {
    void nextTick(() => root.value?.querySelector<HTMLElement>(`[data-field="${field}"]`)?.focus());
  }

  function fail(field: string, message: string): void {
    error.value = { field, message };
    focusField(field);
  }

  async function saveHawb(): Promise<void> {
    if (savingHawb.value) return;
    error.value = null;
    const value = hawb.value.trim();
    if (!value) return fail('hawb', 'Enter the HAWB or BOL number.');
    if (value === ev.value.hawb) return;
    savingHawb.value = true;
    try {
      ev.value = await setHawb(ev.value.id, value);
    } catch (saveError) {
      return fail('hawb', extractErrorMessage(saveError));
    } finally {
      savingHawb.value = false;
    }
    logger.showSuccess(`HAWB/BOL ${value} saved`);
    await store.afterWrite(poNumbers.value);
  }

  async function upload(): Promise<void> {
    if (uploading.value) return;
    error.value = null;
    uploadError.value = null;
    if (!file.value) return fail('file', 'Choose the packing list file.');
    const fileError = packingListFileError(file.value);
    if (fileError) return fail('file', fileError);
    const text = note.value.trim();
    if (versions.value.length > 0 && !text) return fail('note', 'Say what changed in this version.');
    uploading.value = true;
    try {
      const list = await uploadPackingList(ev.value.id, file.value, text || null);
      const summary = { id: list.id, version: list.version, fileName: list.fileName, note: list.note, uploadedBy: list.uploadedBy, uploadedAt: list.uploadedAt };
      ev.value = { ...ev.value, packingLists: [...ev.value.packingLists, summary] };
      remember(list);
      selectedId.value = list.id;
      file.value = null;
      note.value = '';
      logger.showSuccess(`Packing list v${list.version} added`);
    } catch (saveError) {
      uploadError.value = packingListErrorMessage(saveError);
      return;
    } finally {
      uploading.value = false;
    }
    await store.afterWrite(poNumbers.value);
  }
</script>
